import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AppThunk, RootState } from "store"
import { tenantService } from "services"
import { Tenant } from "@type/tenant.types"

interface TenantState {
	list: Tenant[]
	loading: boolean
	tenantId?: string
	tenantName?: string
}

const initialState: TenantState = {
	list: [],
	loading: false,
	tenantId: "",
	tenantName: "",
}

const tenantSlice = createSlice({
	name: "tenant",
	initialState,
	reducers: {
		getTenantStart: state => {
			state.loading = true
		},
		getTenantSuccess: (state, action: PayloadAction<{ tenantId: string; tenantName: string }>) => {
			state.loading = false
			state.tenantId = action.payload.tenantId
			state.tenantName = action.payload.tenantName
		},
		getTenantFailure: state => {
			state.loading = false
		},
		fetchingTenants: state => {
			state.loading = true
		},
		tenantsFetched: (state, action: PayloadAction<Tenant[]>) => {
			state.loading = false
			state.list = action.payload
		},
		fetchingTenantsFailed: state => {
			state.loading = false
		},

		clearTenantState: () => initialState,
	},
})

export const {
	getTenantStart,
	getTenantSuccess,
	getTenantFailure,
	fetchingTenants,
	fetchingTenantsFailed,
	tenantsFetched,
	clearTenantState,
} = tenantSlice.actions

export const getTenantByName =
	(tenantName: string): AppThunk =>
	async dispatch => {
		try {
			dispatch(getTenantStart())
			const { data: { _id: tenantId = "" } = {} } = await tenantService.fetchTenantByName(tenantName)
			dispatch(getTenantSuccess({ tenantId, tenantName }))
		} catch (error) {
			dispatch(getTenantFailure())
			console.error("Error fetching tenantId:", error)
		}
	}

export const getAllTenants = (): AppThunk => async dispatch => {
	try {
		dispatch(fetchingTenants())
		const { data: tenantResponse } = await tenantService.getAllTenants()
		dispatch(tenantsFetched(tenantResponse))
	} catch (error) {
		dispatch(fetchingTenantsFailed())
	}
}

export const selectTenantState = (state: RootState) => state.tenant
export const selectTenantLoading = (state: RootState) => selectTenantState(state).loading
export const selectTenantList = (state: RootState) => selectTenantState(state).list
export const selectTenant = (state: RootState) => ({
	tenantId: state.tenant.tenantId,
	tenantName: state.tenant.tenantName,
})

export default tenantSlice.reducer
